import {
  type CalendarEntry,
  type CalendarFeed,
  type EventApproval,
  type EventConflict,
  eventImageUrl,
  hasLevel,
  ladderAccess,
  tagFilterQuery,
  useAccess,
  useApprovalHistory,
  useCalendar,
  useCalendarFeeds,
  useCampuses,
  useCreateEvent,
  useDecideApproval,
  useEvent,
  useEventConflicts,
  useEventInstances,
  useEvents,
  useEventTags,
  useFeedMutations,
  usePendingApprovals,
  useSubmitForApproval,
} from '@church/data'
import {
  faArrowRight,
  faBan,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCopy,
  faFilter,
  faLocationDot,
  faPaperPlane,
  faPlus,
  faStarSolid,
  faTrashCan,
  faTriangleExclamation,
} from '@church/ui-icons'
import { allDayDurationMinutes, allDayWindow } from '@church/domain'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import { RequireAccess } from '../components/RequireAccess.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  IconButton,
  Input,
  ListHeader,
  listRow,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  Tabs,
  Textarea,
} from '../components/ui.tsx'
import { useTabParam } from '../lib/tabs.ts'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { useListDisplay } from '../lib/display.ts'
import { EventTagsAdmin } from '../components/events/EventTagsAdmin.tsx'
import { TagPicker } from '../components/events/TagPicker.tsx'
import { PageAction } from '../components/PageAction.tsx'

/**
 * The church calendar.
 *
 * It renders **instances**, not events: a month grid shows the dates things
 * happen on, and a recurring event is not one cell but twelve. That is also why
 * the month is the unit of navigation — the query window and the grid are the
 * same thing, so there is nothing to keep in sync.
 *
 * Approvals, drafts and subscription links read as lists; each opens a panel
 * with what can be done to it.
 */
export const Route = createFileRoute('/_authed/_shell/events/')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'events', 'viewer')) throw redirect({ to: '/' })
  },
  component: EventsScreen,
})

const TABS = ['Calendar', 'Approvals', 'Tags', 'Subscribe'] as const
type Tab = (typeof TABS)[number]

function EventsScreen() {
  const access = useAccess()
  const tabs = access.has('events', 'administrator') ? TABS : (['Calendar'] as const)
  const [tab, setTab] = useTabParam<Tab>(tabs)

  return (
    <div className="space-y-6">
      <PageHeader title="Calendar" />
      <Tabs tabs={tabs} value={tab} onChange={setTab}>
        {tab === 'Calendar' && <CalendarPanel />}
        {tab === 'Approvals' && <ApprovalsPanel />}
        {tab === 'Tags' && <EventTagsAdmin />}
        {tab === 'Subscribe' && <FeedsPanel />}
      </Tabs>
    </div>
  )
}

/** Midnight on the first of the month, in the reader's own zone. */
const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date: Date, months: number) =>
  new Date(date.getFullYear(), date.getMonth() + months, 1)

/** The Monday on or before a date — the grid starts on a Monday. */
function startOfGrid(monthStart: Date): Date {
  const start = new Date(monthStart)
  const weekday = (start.getDay() + 6) % 7
  start.setDate(start.getDate() - weekday)
  return start
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** What a day's row shows on the phone's agenda; the month grid shows its own. */
const AGENDA_FIELDS = [
  { id: 'time', label: 'Time', default: true },
  { id: 'location', label: 'Where', default: true },
] as const
type AgendaField = (typeof AGENDA_FIELDS)[number]['id']

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

/**
 * The local days a date covers, first to last. Its end is exclusive — a
 * booking to midnight does not spill into the next day — and the walk stops
 * after six weeks, the most a month grid shows.
 */
function daysCovered(entry: CalendarEntry): Date[] {
  const start = new Date(entry.startsAt)
  const last = new Date(Math.max(start.getTime(), new Date(entry.endsAt).getTime() - 1))
  const days: Date[] = []
  for (
    let day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    day <= last && days.length < 42;
    day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1)
  ) {
    days.push(day)
  }
  return days
}

/** What a date's time says: its start, or "All day". */
const timeOf = (entry: CalendarEntry) => (entry.allDay ? 'All day' : clock(entry.startsAt))

function CalendarPanel() {
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const [campusId, setCampusId] = useState('')
  const [creating, setCreating] = useState(false)
  const [filtering, setFiltering] = useState(false)
  const [pickedTags, setPickedTags] = useState<string[]>([])
  const [featuredOnly, setFeaturedOnly] = useState(false)
  const display = useListDisplay('calendar-agenda', AGENDA_FIELDS)

  const { data: campuses } = useCampuses()
  const { data: tagGroups } = useEventTags()
  const hasTags = (tagGroups ?? []).some((group) => group.tags.length > 0)
  const filterCount = pickedTags.length + (featuredOnly ? 1 : 0)
  const tags = tagFilterQuery(pickedTags)

  // Six weeks from the Monday before the 1st: the same window the grid draws,
  // so nothing can be fetched but unrendered or drawn but unfetched.
  const gridStart = startOfGrid(month)
  const gridEnd = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + 42)

  const { data: entries, isPending } = useCalendar({
    from: gridStart.toISOString(),
    to: gridEnd.toISOString(),
    ...(campusId ? { campusId } : {}),
    ...(tags ? { tags } : {}),
    ...(featuredOnly ? { featured: true } : {}),
  })

  // Featured events this month, once each, for the strip above the grid.
  const featured = [
    ...new Map(
      (entries ?? [])
        .filter(
          (entry) =>
            entry.featured &&
            entry.status !== 'cancelled' &&
            new Date(entry.startsAt).getMonth() === month.getMonth(),
        )
        .map((entry) => [entry.eventId, entry]),
    ).values(),
  ]

  // A date that runs over several days shows on each of them — a weekend
  // away is on Saturday and Sunday, not just the day it starts.
  const byDay = new Map<string, CalendarEntry[]>()
  for (const entry of entries ?? []) {
    for (const day of daysCovered(entry)) {
      const key = day.toDateString()
      byDay.set(key, [...(byDay.get(key) ?? []), entry])
    }
  }

  const days = Array.from(
    { length: 42 },
    (_, index) =>
      new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index),
  )
  const agendaDays = days.filter(
    (day) => day.getMonth() === month.getMonth() && byDay.has(day.toDateString()),
  )

  const today = new Date()

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* On a phone the month and its arrows take a row of their own. */}
        <div className="flex w-full items-center justify-between gap-1 sm:w-auto">
          <IconButton
            icon={faChevronLeft}
            label="Previous month"
            onClick={() => setMonth(addMonths(month, -1))}
          />
          <h2 className="text-center text-base font-semibold text-content-default sm:min-w-44">
            {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </h2>
          <IconButton
            icon={faChevronRight}
            label="Next month"
            onClick={() => setMonth(addMonths(month, 1))}
          />
        </div>
        <Button variant="ghost" onClick={() => setMonth(startOfMonth(new Date()))}>
          Today
        </Button>

        {(campuses ?? []).length > 0 && (
          <Select
            aria-label="Campus"
            className="min-w-0 flex-1 sm:ml-auto sm:max-w-52 sm:flex-none"
            value={campusId}
            onChange={(event) => setCampusId(event.target.value)}
          >
            <option value="">All campuses</option>
            {(campuses ?? []).map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.name}
              </option>
            ))}
          </Select>
        )}

        <Button
          variant={filterCount > 0 ? 'secondary' : 'ghost'}
          icon={faFilter}
          aria-label={filterCount > 0 ? `Filter, ${filterCount} on` : 'Filter'}
          onClick={() => setFiltering(true)}
        >
          {filterCount > 0 ? <span className="tabular-nums">{filterCount}</span> : null}
        </Button>
        <DisplayOptions fields={AGENDA_FIELDS} shown={display.shown} onChange={display.set} />
        <RequireAccess module="events" level="editor">
          <PageAction icon={faPlus} label="New event" onClick={() => setCreating(true)} />
        </RequireAccess>
      </div>

      <NewEventForm open={creating} onDone={() => setCreating(false)} />

      <SidePanel open={filtering} onOpenChange={setFiltering} title="Filter the calendar">
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            checked={featuredOnly}
            onChange={(event) => setFeaturedOnly(event.target.checked)}
          />
          Featured events only
        </label>
        {hasTags ? (
          <TagPicker groups={tagGroups ?? []} picked={pickedTags} onChange={setPickedTags} />
        ) : (
          <p className="text-sm text-content-muted">
            No event tags yet. Whoever runs the calendar adds them under Tags.
          </p>
        )}
        {filterCount > 0 && (
          <Button
            variant="ghost"
            onClick={() => {
              setPickedTags([])
              setFeaturedOnly(false)
            }}
          >
            Clear the filter
          </Button>
        )}
      </SidePanel>

      {!isPending && featured.length > 0 && !featuredOnly && (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {featured.slice(0, 3).map((entry) => (
            <li key={entry.id}>
              <FeaturedCard entry={entry} />
            </li>
          ))}
        </ul>
      )}

      {isPending ? (
        <Spinner />
      ) : (
        <Card className="hidden overflow-hidden sm:block">
          <div className="grid grid-cols-7 border-b border-border-subtle bg-surface-sunken">
            {WEEKDAYS.map((day) => (
              <div key={day} className="p-2 text-center text-xs font-medium text-content-subtle">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const inMonth = day.getMonth() === month.getMonth()
              const entriesToday = byDay.get(day.toDateString()) ?? []
              return (
                <div
                  key={day.toISOString()}
                  className={`min-h-24 border-b border-r border-border-subtle p-1 ${
                    inMonth ? '' : 'bg-surface-sunken/40'
                  }`}
                >
                  <div
                    className={`mb-1 inline-flex size-6 items-center justify-center rounded-full text-xs tabular-nums ${
                      sameDay(day, today)
                        ? 'bg-brand-solid font-semibold text-brand-on-solid'
                        : inMonth
                          ? 'text-content-muted'
                          : 'text-content-subtle'
                    }`}
                    {...(sameDay(day, today) ? { 'aria-label': `Today, ${day.getDate()}` } : {})}
                  >
                    {day.getDate()}
                  </div>
                  <ul className="space-y-0.5">
                    {entriesToday.slice(0, 3).map((entry) => (
                      <li key={entry.id}>
                        <DayEntry entry={entry} />
                      </li>
                    ))}
                    {entriesToday.length > 3 && (
                      <li className="px-1 text-xs text-content-subtle">
                        +{entriesToday.length - 3} more
                      </li>
                    )}
                  </ul>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* A phone has no room for seven columns: the month as one list of days instead. */}
      {!isPending && agendaDays.length > 0 && (
        <Card bleed className="sm:hidden">
          {agendaDays.map((day) => (
            <section key={day.toISOString()}>
              <ListHeader
                action={sameDay(day, today) ? <Chip tone="brand">Today</Chip> : undefined}
              >
                {day.toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </ListHeader>
              <ul className="divide-y divide-border-subtle">
                {(byDay.get(day.toDateString()) ?? []).map((entry) => (
                  <li key={entry.id}>
                    <AgendaEntry entry={entry} hide={display.hide} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </Card>
      )}

      {!isPending && (entries ?? []).length === 0 && (
        <EmptyState
          title="Nothing on this month"
          hint="Events you create appear here on every date they happen."
        />
      )}
    </div>
  )
}

/** One thing on the phone's list of days: its time, its name, where. */
function AgendaEntry({
  entry,
  hide,
}: {
  entry: CalendarEntry
  hide: (field: AgendaField) => string
}) {
  const cancelled = entry.status === 'cancelled'
  return (
    <Link
      to="/events/instances/$id"
      params={{ id: entry.id }}
      className={`${listRow} flex items-center gap-3 no-underline hover:bg-surface-sunken`}
    >
      <span
        className={`w-16 shrink-0 text-sm font-medium tabular-nums text-content-muted ${hide('time')}`}
      >
        {timeOf(entry)}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-base font-medium ${
            cancelled ? 'text-content-subtle line-through' : 'text-content-default'
          }`}
        >
          {entry.featured && (
            <Icon
              icon={faStarSolid}
              label="Featured"
              className="mr-1.5 inline size-3 text-content-brand"
            />
          )}
          {entry.title ?? entry.eventTitle}
        </span>
        {entry.location && (
          <span
            className={`flex items-center gap-1 truncate text-sm text-content-muted ${hide('location')}`}
          >
            <Icon icon={faLocationDot} className="size-3" />
            {entry.location}
          </span>
        )}
      </span>
      {cancelled && <Chip tone="danger">Cancelled</Chip>}
    </Link>
  )
}

function DayEntry({ entry }: { entry: CalendarEntry }) {
  const cancelled = entry.status === 'cancelled'
  const time = timeOf(entry)

  return (
    <Link
      to="/events/instances/$id"
      params={{ id: entry.id }}
      title={`${time} · ${entry.title ?? entry.eventTitle}${entry.location ? ` · ${entry.location}` : ''}`}
      className={`block truncate rounded-r border-l-2 px-1.5 py-0.5 text-xs no-underline transition-colors duration-(--duration-fast) ${
        cancelled
          ? 'border-border-default text-content-subtle line-through'
          : 'border-brand-solid bg-brand-soft hover:bg-surface-sunken'
      }`}
    >
      {entry.featured && (
        <Icon
          icon={faStarSolid}
          label="Featured"
          className="mr-0.5 inline size-2.5 text-content-brand"
        />
      )}
      <span className="text-content-muted tabular-nums">{time}</span>{' '}
      <span className={cancelled ? '' : 'font-medium text-content-default'}>
        {entry.title ?? entry.eventTitle}
      </span>
    </Link>
  )
}

/** A featured event: its picture, its name, when. */
function FeaturedCard({ entry }: { entry: CalendarEntry }) {
  return (
    <Link
      to="/events/instances/$id"
      params={{ id: entry.id }}
      className="flex h-full overflow-hidden rounded-xl border border-border-subtle bg-surface-raised no-underline hover:bg-surface-sunken"
    >
      {entry.imageVersion ? (
        <img
          src={eventImageUrl(entry.eventId, entry.imageVersion)}
          alt=""
          className="w-24 shrink-0 object-cover sm:w-28"
        />
      ) : (
        <span className="flex w-16 shrink-0 items-center justify-center bg-brand-soft text-content-brand">
          <Icon icon={faStarSolid} />
        </span>
      )}
      <span className="min-w-0 flex-1 space-y-0.5 p-3">
        <span className="block text-xs font-semibold uppercase tracking-wide text-content-brand">
          Featured
        </span>
        <span className="block truncate text-base font-medium text-content-default">
          {entry.title ?? entry.eventTitle}
        </span>
        <span className="block truncate text-sm text-content-muted">
          {daysCovered(entry)
            .filter((_, index, all) => index === 0 || index === all.length - 1)
            .map((day) =>
              day.toLocaleDateString(undefined, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
              }),
            )
            .join(' – ')}
          {entry.allDay ? '' : ` · ${clock(entry.startsAt)}`}
        </span>
      </span>
    </Link>
  )
}

function NewEventForm({ open, onDone }: { open: boolean; onDone: () => void }) {
  const create = useCreateEvent()
  const { data: campuses } = useCampuses()
  const blank = {
    title: '',
    description: '',
    location: '',
    campusId: '',
    visibility: 'members' as 'public' | 'members' | 'staff',
    date: new Date().toISOString().slice(0, 10),
    startTime: '19:00',
    endTime: '21:00',
    repeats: 'once' as 'once' | 'weekly' | 'monthly',
    allDay: false,
    lastDay: '',
    featured: false,
    linkUrl: '',
    linkLabel: '',
    tagIds: [] as string[],
  }
  const [form, setForm] = useState(blank)
  const { data: tagGroups } = useEventTags()
  const created = () => {
    setForm(blank)
    onDone()
  }

  const set =
    (
      key:
        | 'title'
        | 'description'
        | 'location'
        | 'campusId'
        | 'date'
        | 'startTime'
        | 'endTime'
        | 'lastDay'
        | 'linkUrl'
        | 'linkLabel'
        | 'visibility'
        | 'repeats',
    ) =>
    (value: string) =>
      setForm((current) => ({ ...current, [key]: value }))

  const submit = () => {
    const base = {
      title: form.title.trim(),
      description: form.description.trim() === '' ? null : form.description.trim(),
      location: form.location.trim() === '' ? null : form.location.trim(),
      campusId: form.campusId === '' ? null : form.campusId,
      visibility: form.visibility,
      status: 'published' as const,
      allDay: form.allDay,
      featured: form.featured,
      linkUrl: form.linkUrl.trim() === '' ? null : form.linkUrl.trim(),
      linkLabel: form.linkLabel.trim() === '' ? null : form.linkLabel.trim(),
      tagIds: form.tagIds,
    }
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const lastDay = form.lastDay === '' ? form.date : form.lastDay

    if (form.allDay) {
      // Midnight to midnight where the person planning it is.
      if (form.repeats === 'once') {
        const window = allDayWindow(form.date, lastDay, zone)
        create.mutate(
          { ...base, startsAt: window.startsAt.toISOString(), endsAt: window.endsAt.toISOString() },
          { onSuccess: created },
        )
      } else {
        create.mutate(
          {
            ...base,
            schedule: {
              start: `${form.date}T00:00`,
              durationMinutes: allDayDurationMinutes(form.date, lastDay),
              timeZone: zone,
              rule: { freq: form.repeats === 'weekly' ? 'WEEKLY' : 'MONTHLY' },
            },
          },
          { onSuccess: created },
        )
      }
      return
    }

    if (form.repeats === 'once') {
      create.mutate(
        {
          ...base,
          startsAt: new Date(`${form.date}T${form.startTime}`).toISOString(),
          endsAt: new Date(`${form.date}T${form.endTime}`).toISOString(),
        },
        { onSuccess: created },
      )
      return
    }

    const minutes =
      (Number(form.endTime.slice(0, 2)) - Number(form.startTime.slice(0, 2))) * 60 +
      (Number(form.endTime.slice(3, 5)) - Number(form.startTime.slice(3, 5)))

    create.mutate(
      {
        ...base,
        schedule: {
          // Wall time plus the browser's zone: "7pm" stays 7pm when the clocks
          // change, which is what a church means by a weekly meeting.
          start: `${form.date}T${form.startTime}`,
          durationMinutes: Math.max(15, minutes),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          rule: { freq: form.repeats === 'weekly' ? 'WEEKLY' : 'MONTHLY' },
        },
      },
      { onSuccess: created },
    )
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone()
      }}
      title="New event"
      primary={
        <Button
          icon={faCheck}
          className="py-1.5"
          pending={create.isPending}
          pendingLabel="Saving…"
          disabled={form.title.trim() === ''}
          onClick={submit}
        >
          Create
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Title">
          <Input
            placeholder="Carols by candlelight"
            value={form.title}
            onChange={(event) => set('title')(event.target.value)}
          />
        </Field>
        <Field label="Where">
          <Input value={form.location} onChange={(event) => set('location')(event.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            checked={form.allDay}
            onChange={(event) => setForm((f) => ({ ...f, allDay: event.target.checked }))}
          />
          All day
        </label>
        {form.allDay ? (
          <div className="grid grid-cols-2 gap-2">
            <Field label="First day">
              <Input
                type="date"
                value={form.date}
                onChange={(event) => set('date')(event.target.value)}
              />
            </Field>
            <Field label="Last day">
              <Input
                type="date"
                min={form.date}
                value={form.lastDay === '' ? form.date : form.lastDay}
                onChange={(event) => set('lastDay')(event.target.value)}
              />
            </Field>
          </div>
        ) : (
          <>
            <Field label="Date">
              <Input
                type="date"
                value={form.date}
                onChange={(event) => set('date')(event.target.value)}
              />
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="From">
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(event) => set('startTime')(event.target.value)}
                />
              </Field>
              <Field label="To">
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(event) => set('endTime')(event.target.value)}
                />
              </Field>
            </div>
          </>
        )}
        <Field label="Repeats">
          <Select
            value={form.repeats}
            onChange={(event) => set('repeats')(event.target.value as typeof form.repeats)}
          >
            <option value="once">Just once</option>
            <option value="weekly">Every week</option>
            <option value="monthly">Every month</option>
          </Select>
        </Field>
        <Field
          label="Who can see it"
          description={
            form.visibility === 'public'
              ? 'Anyone, including the public feed and the website.'
              : form.visibility === 'members'
                ? 'Signed-in members of this church.'
                : 'Staff only — never leaves the office.'
          }
        >
          <Select
            value={form.visibility}
            onChange={(event) => set('visibility')(event.target.value as typeof form.visibility)}
          >
            <option value="public">Public</option>
            <option value="members">Members</option>
            <option value="staff">Staff</option>
          </Select>
        </Field>
        {(campuses ?? []).length > 0 && (
          <Field label="Campus" description="Leave blank for the whole church.">
            <Select value={form.campusId} onChange={(event) => set('campusId')(event.target.value)}>
              <option value="">All campuses</option>
              {(campuses ?? []).map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Description">
          <Textarea
            rows={3}
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
          />
        </Field>
        <label className="flex items-start gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            className="mt-1"
            checked={form.featured}
            onChange={(event) => setForm((f) => ({ ...f, featured: event.target.checked }))}
          />
          <span>
            Featured
            <span className="block text-xs text-content-muted">
              Shown first, at the top of the calendar.
            </span>
          </span>
        </label>
        <PanelSection title="A button to somewhere else" hint="A sign-up form, a ticket page.">
          <Input
            aria-label="Link"
            placeholder="https://…"
            value={form.linkUrl}
            onChange={(event) => set('linkUrl')(event.target.value)}
          />
          <Input
            aria-label="Button label"
            placeholder="Learn more"
            maxLength={40}
            value={form.linkLabel}
            onChange={(event) => set('linkLabel')(event.target.value)}
          />
        </PanelSection>
        {(tagGroups ?? []).some((group) => group.tags.length > 0) && (
          <TagPicker
            groups={tagGroups ?? []}
            picked={form.tagIds}
            onChange={(tagIds) => setForm((f) => ({ ...f, tagIds }))}
          />
        )}

        <ErrorNote error={create.error} />
      </div>
    </SidePanel>
  )
}

/**
 * Subscription links.
 *
 * The URL is the credential — anybody holding it reads the calendar, forever,
 * with no sign-in — so the screen says that where somebody is about to copy
 * one, not in a help page.
 */
function FeedsPanel() {
  const { data: feeds, isPending } = useCalendarFeeds()
  const [creating, setCreating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [shownId, setShownId] = useState<string | null>(null)
  const shown = feeds?.find((feed) => feed.id === shownId)

  if (isPending) return <Spinner />

  return (
    <div className="space-y-4">
      <div className="flex justify-end max-sm:hidden">
        <PageAction icon={faPlus} label="New link" onClick={() => setCreating(true)} />
      </div>

      {!feeds || feeds.length === 0 ? (
        <EmptyState
          title="No subscription links yet"
          hint="A link lets people add the church calendar to Google Calendar, Apple Calendar or Outlook."
        />
      ) : (
        <Card bleed>
          <ul className="divide-y divide-border-subtle">
            {feeds.map((feed) => (
              <li key={feed.id}>
                <button
                  type="button"
                  onClick={() => {
                    setShownId(feed.id)
                    setOpenId(feed.id)
                  }}
                  className={`${listRow} flex w-full flex-wrap items-center gap-2 text-left hover:bg-surface-sunken`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base font-medium text-content-default">
                      {feed.name}
                    </span>
                    <span className="block text-sm text-content-muted">
                      {feed.lastAccessedAt
                        ? `Last used ${new Date(feed.lastAccessedAt).toLocaleDateString()}`
                        : 'Never used'}
                    </span>
                  </span>
                  {feed.includeMembersOnly && <Chip tone="warning">Members-only</Chip>}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <NewFeedForm open={creating} onDone={() => setCreating(false)} />
      <FeedPanel
        feed={shown}
        open={openId !== null && shown !== undefined}
        onClose={() => setOpenId(null)}
      />
    </div>
  )
}

function NewFeedForm({ open, onDone }: { open: boolean; onDone: () => void }) {
  const { create } = useFeedMutations()
  const [name, setName] = useState('')
  const [includeMembersOnly, setIncludeMembersOnly] = useState(false)

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onDone()
      }}
      title="New subscription link"
      description="Anyone with the link reads the calendar, without signing in."
      primary={
        <Button
          icon={faPlus}
          className="py-1.5"
          pending={create.isPending}
          disabled={name.trim() === ''}
          onClick={() =>
            create.mutate(
              { name: name.trim(), includeMembersOnly },
              {
                onSuccess: () => {
                  setName('')
                  setIncludeMembersOnly(false)
                  onDone()
                },
              },
            )
          }
        >
          Create link
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Name" description="What this link is for: “Website — what’s on”.">
          <Input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <label className="flex items-start gap-2 text-sm text-content-default">
          <input
            type="checkbox"
            className="mt-1"
            checked={includeMembersOnly}
            onChange={(event) => setIncludeMembersOnly(event.target.checked)}
          />
          <span>
            Include members-only events.
            <span className="block text-xs text-content-muted">
              Anyone with the link sees them, without signing in. Share it the way you would share a
              key.
            </span>
          </span>
        </label>
        <ErrorNote error={create.error} />
      </div>
    </SidePanel>
  )
}

function FeedPanel({
  feed,
  open,
  onClose,
}: {
  feed: CalendarFeed | undefined
  open: boolean
  onClose: () => void
}) {
  const { revoke } = useFeedMutations()
  const [copied, setCopied] = useState('')
  if (!feed) return null
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={feed.name}
      description={
        feed.lastAccessedAt
          ? `Last used ${new Date(feed.lastAccessedAt).toLocaleDateString()}`
          : 'Never used'
      }
      footer={
        <ConfirmButton
          label="Revoke link"
          icon={faTrashCan}
          variant="quietDanger"
          className="-ml-4"
          title={`Revoke “${feed.name}”?`}
          description="Anyone already subscribed stops receiving updates. This is the only way to un-share a link that has been sent out."
          confirmLabel="Revoke link"
          onConfirm={() => revoke.mutate(feed.id, { onSuccess: onClose })}
        />
      }
    >
      <PanelSection
        title="The link"
        hint="Paste it into Google Calendar, Apple Calendar or Outlook as a subscription."
      >
        <div className="flex gap-2">
          <Input readOnly value={feed.url} aria-label="Link" className="font-mono text-xs" />
          <IconButton
            icon={copied === feed.id ? faCheck : faCopy}
            label={copied === feed.id ? 'Copied' : 'Copy link'}
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(feed.url)
              setCopied(feed.id)
            }}
          />
        </div>
      </PanelSection>
      <PanelSection title="What it shows">
        {feed.includeMembersOnly ? (
          <Chip tone="warning" size="lg">
            Members-only events too
          </Chip>
        ) : (
          <Chip size="lg">Public events</Chip>
        )}
      </PanelSection>
      <ErrorNote error={revoke.error} />
    </SidePanel>
  )
}

/**
 * The approver's screen, and the planner's.
 *
 * Two lists rather than one, because they answer different questions. The top
 * is "what is waiting for me" — the reason an approver opens this tab at all.
 * The bottom is "what have I not sent yet", which is the other half of the
 * workflow and would otherwise be invisible: a draft nobody submitted looks
 * exactly like a draft somebody is still writing.
 */
function ApprovalsPanel() {
  const { data: waiting, isPending, error } = usePendingApprovals()
  const { data: drafts } = useEvents({ status: 'draft' })
  const [approvalId, setApprovalId] = useState<string | null>(null)
  const [shownApproval, setShownApproval] = useState<string | null>(null)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [shownDraft, setShownDraft] = useState<string | null>(null)
  const approval = waiting?.find((request) => request.id === shownApproval)
  const draft = drafts?.find((event) => event.id === shownDraft)

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-content-default">Waiting for an answer</h2>
          <p className="text-sm text-content-muted">
            Nothing here is on the calendar yet. Until one of these is approved, the people planning
            it are the only ones who can see it.
          </p>
        </div>
        <ErrorNote error={error} />
        {isPending ? (
          <Spinner />
        ) : waiting && waiting.length > 0 ? (
          <Card bleed>
            <ul className="divide-y divide-border-subtle">
              {waiting.map((request) => (
                <li key={request.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setShownApproval(request.id)
                      setApprovalId(request.id)
                    }}
                    className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-base font-medium text-content-default">
                        {request.eventTitle}
                      </span>
                      <span className="block truncate text-sm text-content-muted">
                        Asked {new Date(request.requestedAt).toLocaleDateString()}
                        {request.requestNote ? ` · “${request.requestNote}”` : ''}
                      </span>
                    </span>
                    <Chip tone="warning">Waiting</Chip>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState
            title="Nothing waiting"
            hint="Every event that has been sent for approval has had an answer."
          />
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-content-default">Drafts nobody has sent yet</h2>
          <p className="text-sm text-content-muted">
            A draft stays invisible until it is approved. If one of these is ready, send it.
          </p>
        </div>
        {drafts && drafts.length > 0 ? (
          <Card bleed>
            <ul className="divide-y divide-border-subtle">
              {drafts.map((event) => (
                <li key={event.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setShownDraft(event.id)
                      setDraftId(event.id)
                    }}
                    className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                  >
                    <span className="min-w-0 flex-1 truncate text-base font-medium text-content-default">
                      {event.title}
                    </span>
                    <Chip>Draft</Chip>
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState title="No drafts" hint="Nothing is half-planned." />
        )}
      </section>

      <ApprovalPanel
        request={approval}
        open={approvalId !== null && approval !== undefined}
        onClose={() => setApprovalId(null)}
      />
      <DraftPanel
        id={draft?.id}
        title={draft?.title ?? ''}
        open={draftId !== null && draft !== undefined}
        onClose={() => setDraftId(null)}
      />
    </div>
  )
}

/** What the event is: when, where, who sees it — and a link to its first date. */
function EventFacts({ eventId }: { eventId: string }) {
  const { data: event } = useEvent(eventId)
  const { data: instances } = useEventInstances(eventId)
  const first = instances?.[0]
  if (!event) return <Spinner />
  return (
    <>
      <PanelSection title="When and where">
        <p className="text-base text-content-default">
          {event.nextOccurrenceAt
            ? new Date(event.nextOccurrenceAt).toLocaleString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                hour: 'numeric',
                minute: '2-digit',
              })
            : 'No dates yet'}
        </p>
        <div className="flex flex-wrap gap-2">
          {event.instanceCount > 1 && <Chip>{event.instanceCount} dates</Chip>}
          {event.location && (
            <Chip>
              <Icon icon={faLocationDot} className="size-3" />
              {event.location}
            </Chip>
          )}
          <Chip tone={event.visibility === 'public' ? 'brand' : 'neutral'}>
            {event.visibility === 'public'
              ? 'Public'
              : event.visibility === 'members'
                ? 'Members'
                : 'Staff only'}
          </Chip>
        </div>
      </PanelSection>
      {event.description && (
        <PanelSection title="About it">
          <p className="text-sm whitespace-pre-line text-content-default">{event.description}</p>
        </PanelSection>
      )}
      {first && (
        <Link
          to="/events/instances/$id"
          params={{ id: first.id }}
          className="flex items-center gap-3 rounded-lg px-1 py-2 text-sm font-medium text-content-brand hover:bg-surface-sunken"
        >
          <span className="flex-1">Open the event</span>
          <Icon icon={faArrowRight} />
        </Link>
      )}
    </>
  )
}

/** One request, and the two answers an approver can give it. */
function ApprovalPanel({
  request,
  open,
  onClose,
}: {
  request: EventApproval | undefined
  open: boolean
  onClose: () => void
}) {
  const decide = useDecideApproval()
  const [note, setNote] = useState('')
  const { data: history } = useApprovalHistory(request?.eventId ?? '', open)
  const conflicts = useEventConflicts(request?.eventId ?? '', open)
  if (!request) return null
  const earlier = (history ?? []).filter((h) => h.id !== request.id && h.decidedAt)
  const clashes = conflicts.data ?? []

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={request.eventTitle}
      description={`Sent for approval ${new Date(request.requestedAt).toLocaleDateString()}`}
      primary={
        <Button
          variant="success"
          icon={clashes.length > 0 ? faTriangleExclamation : faCheck}
          className="py-1.5"
          pending={decide.isPending}
          onClick={() =>
            decide.mutate({ eventId: request.eventId, action: 'approve' }, { onSuccess: onClose })
          }
        >
          {clashes.length > 0 ? 'Approve anyway' : 'Approve'}
        </Button>
      }
    >
      {request.requestNote && (
        <PanelSection title="Their note">
          <p className="text-sm text-content-default">“{request.requestNote}”</p>
        </PanelSection>
      )}
      <EventFacts key={request.eventId} eventId={request.eventId} />
      <Clashes clashes={clashes} loading={conflicts.isPending} />
      {clashes.length > 0 && (
        <p className="text-sm text-content-muted">
          Approving publishes it anyway — two events can share a room on purpose.
        </p>
      )}
      {earlier.length > 0 && (
        <PanelSection title="Asked before">
          <ul className="space-y-2">
            {earlier.map((h) => (
              <li key={h.id} className="text-sm">
                <Chip tone={h.decision === 'approved' ? 'success' : 'danger'}>{h.decision}</Chip>{' '}
                <span className="text-content-muted">
                  {h.decidedAt ? new Date(h.decidedAt).toLocaleDateString() : ''}
                  {h.decisionNote ? ` — “${h.decisionNote}”` : ''}
                </span>
              </li>
            ))}
          </ul>
        </PanelSection>
      )}
      <PanelSection
        title="Or turn it down"
        hint="This goes back to whoever asked. The event returns to draft so they can fix it and send it again."
      >
        {/* Required, because the person who wrote this event has to know what
            to change. The server refuses an empty one too. */}
        <Textarea
          value={note}
          aria-label="Why not?"
          onChange={(changeEvent) => setNote(changeEvent.target.value)}
          rows={3}
          placeholder="Why not? Clashes with the harvest supper — try the following week?"
        />
        <Button
          variant="danger"
          icon={faBan}
          disabled={decide.isPending || note.trim() === ''}
          onClick={() =>
            decide.mutate(
              { eventId: request.eventId, action: 'reject', note },
              {
                onSuccess: () => {
                  setNote('')
                  onClose()
                },
              },
            )
          }
        >
          Turn it down
        </Button>
      </PanelSection>
      <ErrorNote error={decide.error} />
    </SidePanel>
  )
}

const clashTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
const clashDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })

/**
 * What the event's coming dates clash with: another event in the same place
 * at the same time, or a room of that name held for something else. Each
 * links to what it clashes with; none of them stops the event.
 */
function Clashes({ clashes, loading }: { clashes: readonly EventConflict[]; loading: boolean }) {
  if (loading) return <Spinner label="Checking for clashes…" />
  if (clashes.length === 0)
    return (
      <p className="flex items-center gap-2 text-sm text-content-muted">
        <Icon icon={faCircleCheck} className="size-4 text-success-solid" />
        Nothing else is in the same place at the same time.
      </p>
    )
  return (
    <PanelSection title={`Clashes · ${clashes.length}`}>
      <ul className="divide-y divide-border-subtle rounded-lg border border-warning-solid bg-warning-soft">
        {clashes.map((clash) => {
          const body = (
            <>
              <Icon icon={faTriangleExclamation} className="mt-0.5 size-4 text-warning-content" />
              <span className="min-w-0 flex-1">
                <span className="block font-medium text-warning-content">{clash.title}</span>
                <span className="block text-xs text-warning-content">
                  {clash.kind === 'booking'
                    ? `${clash.place} is booked${clash.bookingStatus === 'pending' ? ' (requested)' : ''}`
                    : `Also in ${clash.place}`}{' '}
                  · {clashDay(clash.otherStartsAt)}, {clashTime(clash.otherStartsAt)}–
                  {clashTime(clash.otherEndsAt)}
                </span>
              </span>
              <Icon icon={faChevronRight} className="mt-1 size-3 text-warning-content" />
            </>
          )
          const rowClass =
            'flex items-start gap-3 px-3 py-2.5 text-sm no-underline hover:bg-warning-soft'
          return (
            <li
              key={`${clash.kind}-${clash.instanceId}-${clash.bookingId ?? clash.otherInstanceId ?? ''}`}
            >
              {clash.kind === 'event' && clash.otherInstanceId ? (
                <Link
                  to="/events/instances/$id"
                  params={{ id: clash.otherInstanceId }}
                  className={rowClass}
                >
                  {body}
                </Link>
              ) : (
                <Link to="/facilities" className={rowClass}>
                  {body}
                </Link>
              )}
            </li>
          )
        })}
      </ul>
    </PanelSection>
  )
}

function DraftPanel({
  id,
  title,
  open,
  onClose,
}: {
  id: string | undefined
  title: string
  open: boolean
  onClose: () => void
}) {
  const submit = useSubmitForApproval()
  const [note, setNote] = useState('')
  const conflicts = useEventConflicts(id ?? '', open)
  if (!id) return null

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={title}
      description="A draft: nobody but its planners can see it."
      primary={
        <Button
          icon={faPaperPlane}
          className="py-1.5"
          pending={submit.isPending}
          onClick={() =>
            submit.mutate(
              { eventId: id, note: note || null },
              {
                onSuccess: () => {
                  setNote('')
                  onClose()
                },
              },
            )
          }
        >
          Send
        </Button>
      }
    >
      <EventFacts key={id} eventId={id} />
      <Clashes clashes={conflicts.data ?? []} loading={conflicts.isPending} />
      <PanelSection title="Send for approval">
        <Textarea
          value={note}
          aria-label="Anything the approver should know?"
          rows={3}
          onChange={(changeEvent) => setNote(changeEvent.target.value)}
          placeholder="Anything the approver should know?"
        />
      </PanelSection>
      <ErrorNote error={submit.error} />
    </SidePanel>
  )
}
